import React from "react";
import Flatpickr from "react-flatpickr";
import {
  Alert,
  Button,
  Col,
  Form,
  FormGroup,
  Input,
  Label,
  Offcanvas,
  OffcanvasBody,
  OffcanvasHeader,
  Row,
} from "reactstrap";

type MonchisFiltros = {
  fechaDesde: string;
  fechaHasta: string;
  estado: string;
  sucursal: string;
  sucursalMapeada: string;
  franquicia: string;
  tipoPago: string;
  importacionId: string;
};

type Props = {
  isOpen: boolean;
  onToggle: () => void;
  filtros: MonchisFiltros;
  onChange: (campo: keyof MonchisFiltros, valor: string) => void;
  onApply: () => void;
  onClear: () => void;
  cargando?: boolean;
  opcionesSucursales: string[];
  opcionesSucursalesMapeadas: string[];
  opcionesFranquicias: string[];
  opcionesTiposPago: string[];
  cargandoOpciones?: boolean;
  errorOpciones?: string | null;
};

const MonchisFiltersDrawer = ({
  isOpen,
  onToggle,
  filtros,
  onChange,
  onApply,
  onClear,
  cargando = false,
  opcionesSucursales = [],
  opcionesSucursalesMapeadas = [],
  opcionesFranquicias = [],
  opcionesTiposPago = [],
  cargandoOpciones = false,
  errorOpciones,
}: Props) => {
  const selectsDisabled = cargando || cargandoOpciones;

  return (
    <Offcanvas direction="end" isOpen={isOpen} toggle={onToggle} scrollable>
      <OffcanvasHeader toggle={onToggle}>Filtros de conciliación Monchis</OffcanvasHeader>
      <OffcanvasBody>
        {errorOpciones && (
          <Alert color="warning" className="mb-3">
            {errorOpciones}
          </Alert>
        )}
        <Form
          onSubmit={(event) => {
            event.preventDefault();
            onApply();
          }}
        >
          <Row className="g-3">
            <Col xs={12}>
              <FormGroup>
                <Label>Seleccionar rango</Label>
                <Flatpickr
                  className="form-control"
                  options={{ mode: "range", dateFormat: "Y-m-d" }}
                  value={[filtros.fechaDesde, filtros.fechaHasta].filter(Boolean)}
                  onChange={(dates) => {
                    const [desde, hasta] = dates;
                    onChange("fechaDesde", desde ? desde.toISOString().slice(0, 10) : "");
                    onChange("fechaHasta", hasta ? hasta.toISOString().slice(0, 10) : "");
                  }}
                  disabled={cargando}
                />
              </FormGroup>
            </Col>
            <Col xs={12}>
              <FormGroup>
                <Label for="monchis-filtro-estado">Estado</Label>
                <Input
                  id="monchis-filtro-estado"
                  type="select"
                  value={filtros.estado}
                  onChange={(event) => onChange("estado", event.target.value)}
                  disabled={cargando}
                >
                  <option value="">Todos</option>
                  <option value="Entregada">Entregada</option>
                  <option value="Cancelada">Cancelada</option>
                  <option value="Rechazada">Rechazada</option>
                </Input>
              </FormGroup>
            </Col>
            <Col xs={12}>
              <FormGroup>
                <Label for="monchis-filtro-sucursal">Sucursal</Label>
                <Input
                  id="monchis-filtro-sucursal"
                  type="select"
                  value={filtros.sucursal}
                  onChange={(event) => onChange("sucursal", event.target.value)}
                  disabled={selectsDisabled}
                >
                  <option value="">Todas</option>
                  {opcionesSucursales.map((sucursal) => (
                    <option key={sucursal} value={sucursal}>
                      {sucursal}
                    </option>
                  ))}
                </Input>
              </FormGroup>
            </Col>
            <Col xs={12}>
              <FormGroup>
                <Label for="monchis-filtro-sucursal-mapeada">Sucursal mapeada</Label>
                <Input
                  id="monchis-filtro-sucursal-mapeada"
                  type="select"
                  value={filtros.sucursalMapeada}
                  onChange={(event) => onChange("sucursalMapeada", event.target.value)}
                  disabled={selectsDisabled}
                >
                  <option value="">Todas</option>
                  {opcionesSucursalesMapeadas.map((sucursal) => (
                    <option key={sucursal} value={sucursal}>
                      {sucursal}
                    </option>
                  ))}
                </Input>
              </FormGroup>
            </Col>
            <Col xs={12}>
              <FormGroup>
                <Label for="monchis-filtro-franquicia">Franquicia</Label>
                <Input
                  id="monchis-filtro-franquicia"
                  type="select"
                  value={filtros.franquicia}
                  onChange={(event) => onChange("franquicia", event.target.value)}
                  disabled={selectsDisabled}
                >
                  <option value="">Todas</option>
                  {opcionesFranquicias.map((franquicia) => (
                    <option key={franquicia} value={franquicia}>
                      {franquicia}
                    </option>
                  ))}
                </Input>
              </FormGroup>
            </Col>
            <Col xs={12}>
              <FormGroup>
                <Label for="monchis-filtro-tipopago">Tipo de pago</Label>
                <Input
                  id="monchis-filtro-tipopago"
                  type="select"
                  value={filtros.tipoPago}
                  onChange={(event) => onChange("tipoPago", event.target.value)}
                  disabled={selectsDisabled}
                >
                  <option value="">Todos</option>
                  {opcionesTiposPago.map((tipo) => (
                    <option key={tipo} value={tipo}>
                      {tipo}
                    </option>
                  ))}
                </Input>
              </FormGroup>
            </Col>
            <Col xs={12}>
              <FormGroup>
                <Label for="monchis-filtro-importacion">Importación #</Label>
                <Input
                  id="monchis-filtro-importacion"
                  type="number"
                  min="1"
                  value={filtros.importacionId}
                  onChange={(event) => onChange("importacionId", event.target.value)}
                  disabled={cargando}
                />
              </FormGroup>
            </Col>
          </Row>
          <div className="d-flex flex-wrap gap-2 mt-3">
            <Button color="primary" type="submit" disabled={cargando}>
              Aplicar filtros
            </Button>
            <Button color="light" type="button" onClick={onClear} disabled={cargando}>
              Limpiar
            </Button>
          </div>
        </Form>
      </OffcanvasBody>
    </Offcanvas>
  );
};

export default MonchisFiltersDrawer;
